import { Stack, Box } from '@mui/material';
import { Typography, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import FilesList from './FilesList';
import FilesUploadButton from './FilesUploadButton';
import { AgentFile, AgentFileInvalid } from '../aiAgentsConsts';
import { useState } from 'react';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { sortByDate } from '../../../utils/sortByDate';
import { SortOrder } from '../../common/types';
import { Close, PlusFile } from '@sweep-io/sweep-design/dist/icons';
import useCrmOrgPermissions from '../../common/permissions/useCrmOrgPermissions';

const AgentFiles = ({
  files,
  addFiles,
  removeFile,
  agentName,
  closeDialog,
}: {
  files: AgentFile[];
  addFiles: (files: File[]) => Promise<void>;
  removeFile: (fileIdToRemove: string) => void;
  agentName: string;
  closeDialog: () => void;
}) => {
  const [invalidFiles, setInvalidFiles] = useState<AgentFileInvalid[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAllowedToEditAgent] = useCrmOrgPermissions(['edit:ai-agent']);

  const onFilesAdd = async (addedFiles: File[]) => {
    if (!isAllowedToEditAgent) return;
    setIsLoading(true);
    await addFiles(addedFiles);
    setIsLoading(false);
  };

  const onRemove = (fileIdToRemove: string) => {
    if (!isAllowedToEditAgent) return;
    removeFile(fileIdToRemove);
  };

  const onRemoveInvalidFile = (fileIdToRemove: string) => {
    setInvalidFiles(invalidFiles.filter((file) => file.id !== fileIdToRemove));
  };

  const onFilesAddInvalid = (files: AgentFileInvalid[]) => {
    setInvalidFiles(files);
  };

  const renderEmptyState = files.length === 0;
  const sortedFiles = files.toSorted((a, b) =>
    sortByDate(a.createdAt, b.createdAt, SortOrder.DESC),
  );

  return (
    <Stack flex={1} p={2} gap={2}>
      <Stack
        direction="row"
        sx={{
          height: '32px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h3">Files</Typography>
        <Stack direction="row" gap={1} alignItems="center">
          {!renderEmptyState && (
            <Tooltip
              title={
                !isAllowedToEditAgent
                  ? 'To manage agent files, please contact your admin'
                  : undefined
              }
            >
              <span>
                <FilesUploadButton
                  startIconName="Files"
                  onAddValidFiles={onFilesAdd}
                  onAddInvalidFiles={onFilesAddInvalid}
                  size="small"
                  disabled={!isAllowedToEditAgent}
                />
              </span>
            </Tooltip>
          )}
          <IconButton onClick={closeDialog} variant="flat" size="small">
            <Close />
          </IconButton>
        </Stack>
      </Stack>
      {renderEmptyState && (
        <EmptyState
          onAddValidFiles={onFilesAdd}
          onAddInvalidFiles={onFilesAddInvalid}
          agentName={agentName}
          isAllowedToEditAgent={isAllowedToEditAgent}
        />
      )}
      {!renderEmptyState && (
        <>
          <Typography variant="body" color={colors.grey[700]}>
            Uploaded files
          </Typography>
          {isLoading && (
            <Stack pl={1.5} alignItems="start">
              <CenteredCircularProgress size={20} />
            </Stack>
          )}
          <FilesList
            files={sortedFiles}
            invalidFiles={invalidFiles}
            onRemove={onRemove}
            onRemoveInvalidFile={onRemoveInvalidFile}
          />
        </>
      )}
    </Stack>
  );
};

const EmptyState = ({
  onAddValidFiles,
  onAddInvalidFiles,
  agentName,
  isAllowedToEditAgent,
}: {
  onAddValidFiles: (files: File[]) => void;
  onAddInvalidFiles: (agentFiles: AgentFileInvalid[]) => void;
  agentName: string;
  isAllowedToEditAgent: boolean;
}) => {
  return (
    <Stack height="100%" p={2} gap={2} alignItems="center" justifyContent="center">
      <Stack alignItems="center" gap={1}>
        <PlusFile size="medium" />
        <Typography variant="h5" color={colors.grey[800]}>
          Add data, reports, business documents, and more
        </Typography>
        <Box maxWidth="350px" textAlign="center">
          <Typography variant="body" color={colors.grey[700]}>
            {agentName} can access their content when you chat with the agent
          </Typography>
        </Box>
      </Stack>
      <Box>
        <Tooltip
          title={
            !isAllowedToEditAgent ? 'To manage agent files, please contact your admin' : undefined
          }
        >
          <span>
            <FilesUploadButton
              startIconName="Plus"
              onAddValidFiles={onAddValidFiles}
              onAddInvalidFiles={onAddInvalidFiles}
              size="small"
              disabled={!isAllowedToEditAgent}
            />
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
};

export default AgentFiles;
